import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../style/OpenModalButton.css';

import CategoryModal from '../../../modals/category_modal/ui/Main';
import LocationModal from '../../../modals/location_modal/ui/Main';
import FilterModal from '../../../modals/filter_modal/ui/Main';

import rightIcon from '../../../../assets/icons/right_arrow.svg';
import filterIcon from '../../../../assets/icons/filter.svg';
import { t } from 'i18next';

const OpenModalButton = ({ 
    onSelect, 
    type = 'category', 
    selectedCategoryId, 
    selectedSubcategoryId, 
    selectedLocationId,
    placeholder,
    className = ''
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedText, setSelectedText] = useState(placeholder || (type === 'filter' ? t('filters') : t('select')));

    // Get selected text from localStorage or props
    const updateSelectedText = useCallback(() => {
        if (type === 'filter') {
            setSelectedText(t('filters'));
            return;
        }

        if (type === 'location') {
            // For location, you might want to get from localStorage or props
            setSelectedText(t('select'));
            return;
        }

        // For category type
        const storedCategoryName = localStorage.getItem("selectedCategoryName");
        const storedSubcategoryName = localStorage.getItem("selectedSubcategoryName");

        if (storedCategoryName && storedSubcategoryName) {
            setSelectedText(`${storedCategoryName}, ${storedSubcategoryName}`);
        } else {
            setSelectedText(placeholder || t('select'));
        }
    }, [type, placeholder]);

    useEffect(() => {
        updateSelectedText();
    }, [updateSelectedText, selectedCategoryId, selectedSubcategoryId]);

    // Listen for storage changes
    useEffect(() => {
        const handleStorageChange = () => {
            updateSelectedText();
        };

        // Listen to custom storage event (since localStorage doesn't trigger storage event in same tab)
        window.addEventListener('localStorageUpdated', handleStorageChange);

        // Also listen to actual storage events (for cross-tab communication)
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('localStorageUpdated', handleStorageChange);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [updateSelectedText]);

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        document.body.style.overflow = '';
    }, []);

    const handleSelect = useCallback((selection) => {
        // Update selected text immediately
        if (type === 'category') {
            const newCategoryName = localStorage.getItem("selectedCategoryName");
            const newSubcategoryName = localStorage.getItem("selectedSubcategoryName");

            if (newCategoryName && newSubcategoryName) {
                setSelectedText(`${newCategoryName}, ${newSubcategoryName}`);
            } else {
                setSelectedText(placeholder || t('select'));
            }
        }

        // Call parent's onSelect callback
        if (onSelect) {
            onSelect({
                categoryId: selection.category,
                subcategoryId: selection.subcategory,
                filterOption: selection.filterOption,
                location: selection.location
            });
        }

        handleCloseModal();
    }, [type, onSelect, handleCloseModal, placeholder]);

    // Handle ESC key to close modal
    useEffect(() => {
        if (!isModalOpen) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleCloseModal();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isModalOpen, handleCloseModal]);

    const renderModal = () => {
        if (!isModalOpen) return null;

        let modalContent;

        if (type === 'category') {
            modalContent = (
                <CategoryModal 
                    onSelect={handleSelect} 
                    onClose={handleCloseModal}
                    initialSelectedCategoryId={selectedCategoryId}
                    initialSelectedSubcategoryId={selectedSubcategoryId}
                />
            );
        } else if (type === 'location') {
            modalContent = (
                <LocationModal 
                    menuVisible={true} 
                    onSelect={handleSelect} 
                    onClose={handleCloseModal} 
                />
            );
        } else if (type === 'filter') {
            // FilterModal doesn't have overlay, so we need to add it
            modalContent = (
                <>
                    <div onClick={handleCloseModal} className="click navigation_content_background"></div>
                    <div className="navigation_content">
                        <FilterModal 
                            onSelect={handleSelect} 
                            onClose={handleCloseModal} 
                        />
                    </div>
                </>
            );
        }

        // Render modal in portal for better DOM structure
        return createPortal(
            modalContent,
            document.body
        );
    };

    const iconSrc = type === 'filter' ? filterIcon : rightIcon;
    const iconAlt = type === 'filter' ? 'filter' : 'arrow';

    return (
        <>
            <button
                className={`open_modal_button ${type === 'filter' ? 'open_modal_button_filter' : ''} ${className}`}
                onClick={handleOpenModal}
                type="button"
                aria-label={selectedText}
                aria-expanded={isModalOpen}
            >
                <span className="open_modal_button_text">{selectedText}</span>
                <img 
                    src={iconSrc} 
                    alt={iconAlt}
                    className="open_modal_button_icon"
                    aria-hidden="true"
                />
            </button>
            {renderModal()}
        </>
    );
};

export default OpenModalButton;
